// useMention: an @-mention picker inside a text field, on the shared ComboboxList. Typing "@" at
// the start of a word opens the people list filtered by what follows; picking replaces "@query"
// with whatever the host writes (plain "@Ana Ruiz " in a comment, "@[Ana Ruiz](id) " in a
// markdown body) and puts the caret after it. docs/operations/design-standard.md, "Controls".

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ComboboxList, ComboboxOption, DropSide, useDropSide } from './ComboboxList';

let seq = 0;

type TextHost = HTMLTextAreaElement | HTMLInputElement;

export interface UseMentionArgs {
  /** The text field. The hook reads the caret from it and puts it back after a pick. */
  inputRef: React.RefObject<TextHost>;
  /** The box the list hangs from (the field's relative wrapper). Defaults to the field's parent. */
  anchorRef?: React.RefObject<HTMLElement>;
  value: string;
  onChange: (next: string) => void;
  /** The people. `label` is the first name, `sublabel` the surname; both are searched. */
  options: ComboboxOption[];
  /** The text that replaces "@query". */
  insert: (option: ComboboxOption) => string;
  /** After a pick: record who was mentioned. */
  onPick?: (option: ComboboxOption) => void;
  /** Each time a token opens: fetch the people on first use. */
  onOpen?: () => void;
  /** Characters that end a token besides whitespace (markdown's brackets). */
  breaks?: RegExp;
  /** `above` for a composer at the foot of a thread; `auto` takes the side with room. */
  side?: DropSide | 'auto';
  /** Rows shown at most. */
  limit?: number;
  /** Names the list for a reader. */
  label?: string;
}

export interface Mention {
  open: boolean;
  /** An @-token is being typed, even when nothing matches yet. `open` is false in that case. */
  active: boolean;
  /** Spread on the text field. Call your own key handler after it and skip it if defaultPrevented. */
  fieldProps: {
    role: 'combobox';
    'aria-expanded': boolean;
    'aria-controls': string | undefined;
    'aria-activedescendant': string | undefined;
    'aria-autocomplete': 'list';
    onKeyDown: (e: React.KeyboardEvent<TextHost>) => void;
    onSelect: () => void;
    onBlur: () => void;
  };
  /** Render it inside the same relative box as the field. */
  list: React.ReactElement;
}

export function useMention({
  inputRef,
  anchorRef,
  value,
  onChange,
  options,
  insert,
  onPick,
  onOpen,
  breaks,
  side: prefer = 'auto',
  limit = 8,
  label = 'People to mention',
}: UseMentionArgs): Mention {
  const [listId] = useState(() => `ui-mention-${++seq}`);
  const [start, setStart] = useState<number | null>(null);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);

  // Walk back from the caret to an "@" that starts a word. A bare "@" mid-word is an address.
  const refresh = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const caret = el.selectionStart ?? 0;
    for (let i = caret - 1; i >= 0; i -= 1) {
      const ch = value[i];
      if (ch === '@') {
        if (i > 0 && !/\s/.test(value[i - 1])) break;
        const token = value.slice(i + 1, caret);
        if (/\s/.test(token) || (breaks && token.search(breaks) !== -1)) break;
        if (start !== i && onOpen) onOpen();
        if (start !== i) setActive(0);
        setStart(i);
        setQuery(token.toLowerCase());
        return;
      }
      if (/\s/.test(ch)) break;
    }
    setStart(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, start, breaks]);

  useEffect(() => { refresh(); }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => {
    if (start === null) return [];
    const q = query.trim();
    const hit = q ? options.filter((o) => `${o.label} ${o.sublabel || ''}`.toLowerCase().includes(q)) : options;
    return hit.slice(0, limit);
  }, [start, query, options, limit]);

  useEffect(() => { setActive((a) => Math.min(a, Math.max(0, rows.length - 1))); }, [rows.length]);

  const fallback = React.useRef<HTMLElement | null>(null);
  fallback.current = inputRef.current?.parentElement || null;
  const side = useDropSide(anchorRef || fallback, start !== null, prefer);

  const open = start !== null && rows.length > 0;

  const pick = (option: ComboboxOption) => {
    const el = inputRef.current;
    if (start === null || !el) return;
    const caret = el.selectionStart ?? value.length;
    const text = insert(option);
    onChange(value.slice(0, start) + text + value.slice(caret));
    if (onPick) onPick(option);
    const at = start + text.length;
    setStart(null);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(at, at);
    });
  };

  const onKeyDown = (e: React.KeyboardEvent<TextHost>) => {
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, rows.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pick(rows[active]); }
    else if (e.key === 'Escape') {
      // Consumed, so a sheet or dialog around the composer stays open.
      e.preventDefault();
      e.stopPropagation();
      setStart(null);
    }
  };

  return {
    open,
    active: start !== null,
    fieldProps: {
      role: 'combobox',
      'aria-expanded': open,
      'aria-controls': open ? listId : undefined,
      'aria-activedescendant': open ? `${listId}-${active}` : undefined,
      'aria-autocomplete': 'list',
      onKeyDown,
      onSelect: refresh,
      onBlur: () => setStart(null),
    },
    list: (
      <ComboboxList
        id={listId}
        label={label}
        options={rows}
        open={open}
        active={active}
        onActive={setActive}
        onPick={pick}
        side={side}
        layout="inline"
      />
    ),
  };
}

export default useMention;
